import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, RefreshCw, Search, Filter, X, Plus, Save, Tag, Check, MapPin, Clock, BookOpen, Building2, KeyRound } from 'lucide-react';

const SYSTEM_ROLES = ['Faculty', 'AdminStaff', 'ITStaff', 'TimeTableManager', 'CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'];
const ROLE_LABELS = { Faculty: 'Faculty', AdminStaff: 'Admin Staff', ITStaff: 'IT Staff', TimeTableManager: 'Time Table Manager', CIC: 'CIC', AcademicHead: 'Head', PrincipalSSV: 'Principal', SuperAdmin: 'Super Admin', CEO: 'CEO' };
const ROLE_COLORS = { Faculty: 'bg-blue-100 text-blue-700', AdminStaff: 'bg-teal-100 text-teal-700', ITStaff: 'bg-sky-100 text-sky-700', TimeTableManager: 'bg-violet-100 text-violet-700', CIC: 'bg-cyan-100 text-cyan-700', AcademicHead: 'bg-indigo-100 text-indigo-700', PrincipalSSV: 'bg-emerald-100 text-emerald-700', SuperAdmin: 'bg-amber-100 text-amber-700', CEO: 'bg-rose-100 text-rose-700' };

function AssignedCentresSelect({ userId, currentCentres, allCentres, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = currentCentres || [];
  const toggle = (c) => {
    const next = selected.includes(c) ? selected.filter(x => x !== c) : [...selected, c];
    onChange(next);
  };
  return (
    <div className="relative w-full">
      <button
        data-testid={`assigned-centres-btn-${userId}`}
        onClick={() => setOpen(!open)}
        className="text-left w-full min-h-[26px] text-[10px] border border-blue-200 rounded px-1.5 py-1 bg-blue-50 hover:border-blue-400 transition-colors"
      >
        {selected.length > 0 ? (
          <div className="flex flex-wrap gap-0.5">{selected.map(c => <span key={c} className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[9px] font-semibold">{c}</span>)}</div>
        ) : <span className="text-blue-400">+ Access centres</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded shadow-lg p-2 min-w-[140px]">
          {allCentres.map(c => (
            <label key={c} className="flex items-center gap-1.5 text-xs py-0.5 cursor-pointer hover:bg-slate-50 px-1 rounded">
              <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)} />
              <span>{c}</span>
            </label>
          ))}
          <button onClick={() => setOpen(false)} data-testid={`assigned-centres-close-${userId}`} className="mt-1 w-full text-[10px] font-semibold bg-slate-100 text-slate-700 py-1 rounded hover:bg-slate-200">Done</button>
        </div>
      )}
    </div>
  );
}


function InchargeSelect({ userId, currentIds, allUsers, onSave }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(currentIds || []);
  const potentials = allUsers.filter(u => u._id !== userId && ['CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(u.role));

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSave = () => { onSave(selected); setOpen(false); };

  const names = selected.map(id => { const u = allUsers.find(x => x._id === id); return u ? u.name : ''; }).filter(Boolean);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="text-left w-full min-h-[28px] text-xs border border-slate-200 rounded px-1.5 py-1 bg-white hover:border-slate-400 transition-colors">
        {names.length > 0 ? (
          <div className="flex flex-wrap gap-0.5">{names.map((n, i) => <span key={i} className="bg-indigo-50 text-indigo-600 px-1 py-0.5 rounded text-[9px] font-semibold">{n}</span>)}</div>
        ) : <span className="text-slate-400">Select...</span>}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {potentials.map(u => (
            <label key={u._id} className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 text-xs ${selected.includes(u._id) ? 'bg-blue-50' : ''}`}>
              <input type="checkbox" checked={selected.includes(u._id)} onChange={() => toggle(u._id)} className="accent-blue-600 w-3.5 h-3.5" />
              <span className="truncate flex-1">{u.name}</span>
              <span className={`text-[9px] font-bold px-1 rounded ${ROLE_COLORS[u.role] || ''}`}>{ROLE_LABELS[u.role] || u.role}</span>
            </label>
          ))}
          {potentials.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No in-charges available</p>}
          <div className="sticky bottom-0 bg-white border-t px-2 py-1.5 flex justify-end gap-1">
            <button onClick={() => setOpen(false)} className="text-xs text-slate-500 px-2 py-1 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={handleSave} className="text-xs text-white bg-blue-600 px-2 py-1 rounded hover:bg-blue-700 font-semibold">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CoursePicker({ userId, currentIds, allCourses, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    const next = currentIds.includes(id) ? currentIds.filter(x => x !== id) : [...currentIds, id];
    onChange(next);
  };
  const selectedCourses = allCourses.filter(c => currentIds.includes(c._id));

  return (
    <div className="relative">
      <button data-testid={`course-picker-${userId}`} onClick={() => setOpen(!open)} className="text-left w-full min-h-[28px] text-xs border border-cyan-200 rounded px-1.5 py-1 bg-white hover:border-cyan-400 transition-colors">
        {selectedCourses.length > 0 ? (
          <div className="flex flex-wrap gap-0.5">{selectedCourses.map(c => <span key={c._id} className="bg-cyan-50 text-cyan-700 px-1 py-0.5 rounded text-[9px] font-semibold">{c.name}</span>)}</div>
        ) : <span className="text-slate-400">Assign courses...</span>}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-56 bg-white border border-cyan-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {allCourses.map(c => (
            <label key={c._id} data-testid={`course-option-${c._id}`} className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-cyan-50 text-xs ${currentIds.includes(c._id) ? 'bg-cyan-50' : ''}`}>
              <input type="checkbox" checked={currentIds.includes(c._id)} onChange={() => toggle(c._id)} className="accent-cyan-600 w-3.5 h-3.5" />
              <span className="truncate flex-1">{c.name}</span>
              {c.code && <span className="text-[9px] text-cyan-500">{c.code}</span>}
            </label>
          ))}
          {allCourses.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No courses defined.</p>}
          <div className="sticky bottom-0 bg-white border-t px-2 py-1.5 flex justify-end">
            <button onClick={() => setOpen(false)} className="text-xs text-white bg-cyan-600 px-2 py-1 rounded hover:bg-cyan-700 font-semibold">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function UserManagementPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [centres, setCentres] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showCoursesPanel, setShowCoursesPanel] = useState(false);
  const [showCentresPanel, setShowCentresPanel] = useState(false);
  const [newCourse, setNewCourse] = useState({ name: '', code: '' });
  const [newCentre, setNewCentre] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [centreFilter, setCentreFilter] = useState('All');
  const [newDesig, setNewDesig] = useState('');
  const [showDesigPanel, setShowDesigPanel] = useState(false);
  const [showBlockerPanel, setShowBlockerPanel] = useState(false);
  const [blockerDays, setBlockerDays] = useState({});
  const [blockerSaving, setBlockerSaving] = useState({});
  const [editedRows, setEditedRows] = useState({}); // { [userId]: {role, designation, centre, incharge_ids} }
  const [savingRows, setSavingRows] = useState({});
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const canEdit = ['PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [usersRes, statusRes, desigRes, centresRes, blockerRes, coursesRes] = await Promise.all([
        api.getUsers(), api.getSyncStatus(), api.getDesignations(), api.getCentres(), api.getBlockerDays(), api.getCourses()
      ]);
      setUsers(usersRes.data);
      setSyncStatus(statusRes.data);
      setDesignations(desigRes.data);
      setCentres(centresRes.data);
      setBlockerDays(blockerRes.data || {});
      setCourses(coursesRes.data || []);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleAddCourse = async () => {
    if (!newCourse.name.trim()) return;
    try {
      await api.createCourse(newCourse.name.trim(), newCourse.code.trim());
      setNewCourse({ name: '', code: '' });
      const { data } = await api.getCourses();
      setCourses(data || []);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add course'); }
  };

  const handleDeleteCourse = async (id, name) => {
    if (!window.confirm(`Delete course "${name}"? It will be removed from all assigned users.`)) return;
    try {
      await api.deleteCourse(id);
      const [{ data: cs }, { data: us }] = await Promise.all([api.getCourses(), api.getUsers()]);
      setCourses(cs || []);
      setUsers(us);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAddCentre = async () => {
    if (!newCentre.trim()) return;
    try {
      await api.addCentre(newCentre.trim());
      setNewCentre('');
      const { data } = await api.getCentres();
      setCentres(data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add centre'); }
  };

  const handleDeleteCentre = async (name) => {
    if (!window.confirm(`Delete centre "${name}"?`)) return;
    try {
      await api.deleteCentre(name);
      const { data } = await api.getCentres();
      setCentres(data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try { const { data } = await api.syncUsers(); setSyncResult(data); await loadData(); }
    catch (err) { setSyncResult({ message: err.response?.data?.detail || 'Sync failed' }); }
    finally { setSyncing(false); }
  };

  const getEdited = (u) => editedRows[u._id] || {};
  const setField = (userId, field, value) => {
    setEditedRows(prev => ({ ...prev, [userId]: { ...prev[userId], [field]: value } }));
  };

  const hasChanges = (u) => {
    const e = editedRows[u._id];
    if (!e) return false;
    return (e.role !== undefined && e.role !== u.role) ||
           (e.designation !== undefined && e.designation !== (u.designation || '')) ||
           (e.centre !== undefined && e.centre !== (u.centre || '')) ||
           (e.assigned_centres !== undefined && JSON.stringify(e.assigned_centres) !== JSON.stringify(u.assigned_centres || [])) ||
           (e.incharge_ids !== undefined && JSON.stringify(e.incharge_ids) !== JSON.stringify(u.incharge_ids || [])) ||
           (e.course_ids !== undefined && JSON.stringify(e.course_ids) !== JSON.stringify(u.course_ids || [])) ||
           (e.also_faculty !== undefined && e.also_faculty !== !!u.also_faculty) ||
           (e.is_active !== undefined && e.is_active !== (u.is_active !== false));
  };

  const handleSaveRow = async (u) => {
    const e = editedRows[u._id];
    if (!e) return;
    setSavingRows(prev => ({ ...prev, [u._id]: true }));
    try {
      const payload = {
        role: e.role !== undefined ? e.role : u.role,
        designation: e.designation !== undefined ? e.designation : (u.designation || ''),
        centre: e.centre !== undefined ? e.centre : (u.centre || ''),
        incharge_ids: e.incharge_ids !== undefined ? e.incharge_ids : (u.incharge_ids || []),
      };
      if (e.also_faculty !== undefined) payload.also_faculty = e.also_faculty;
      if (user?.role === 'SuperAdmin') {
        payload.course_ids = e.course_ids !== undefined ? e.course_ids : (u.course_ids || []);
        if (e.assigned_centres !== undefined) payload.assigned_centres = e.assigned_centres;
        if (e.is_active !== undefined) payload.is_active = e.is_active;
      }
      await api.updateUserInline(u._id, payload);
      // Reload to get updated course_names from backend
      const { data } = await api.getUsers();
      setUsers(data);
      setEditedRows(prev => { const n = { ...prev }; delete n[u._id]; return n; });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingRows(prev => ({ ...prev, [u._id]: false }));
    }
  };

  const handleAddDesig = async () => {
    if (!newDesig.trim()) return;
    try { await api.createDesignation(newDesig.trim()); setNewDesig(''); const { data } = await api.getDesignations(); setDesignations(data); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to add'); }
  };

  const handleDeleteDesig = async (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.deleteDesignation(name); const { data } = await api.getDesignations(); setDesignations(data); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveBlocker = async (role) => {
    const days = blockerDays[role];
    if (days === undefined || days === null || days === '') return;
    setBlockerSaving(prev => ({ ...prev, [role]: true }));
    try {
      await api.setBlockerDays(role, parseInt(days));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setBlockerSaving(prev => ({ ...prev, [role]: false }));
    }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()) || u.department?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'All' || u.role === roleFilter;
    const matchCentre = centreFilter === 'All' || u.centre === centreFilter;
    return matchSearch && matchRole && matchCentre;
  });

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, roleFilter, centreFilter]);

  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const availableRoles = ['All', ...new Set(users.map(u => u.role).filter(Boolean))];

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading users...</div>;

  return (
    <div data-testid="user-management-page" className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Administration</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>User Management</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && <Button data-testid="manage-designations-btn" onClick={() => { setShowDesigPanel(!showDesigPanel); setShowBlockerPanel(false); setShowCoursesPanel(false); setShowCentresPanel(false); }} variant="outline" className="text-sm border-slate-300"><Tag className="w-4 h-4 mr-1.5" /> Designations</Button>}
          {user?.role === 'SuperAdmin' && <Button data-testid="manage-courses-btn" onClick={() => { setShowCoursesPanel(!showCoursesPanel); setShowDesigPanel(false); setShowBlockerPanel(false); setShowCentresPanel(false); }} variant="outline" className="text-sm border-cyan-300 text-cyan-700 hover:bg-cyan-50"><BookOpen className="w-4 h-4 mr-1.5" /> Courses</Button>}
          {user?.role === 'SuperAdmin' && <Button data-testid="manage-centres-btn" onClick={() => { setShowCentresPanel(!showCentresPanel); setShowDesigPanel(false); setShowBlockerPanel(false); setShowCoursesPanel(false); }} variant="outline" className="text-sm border-blue-300 text-blue-700 hover:bg-blue-50"><Building2 className="w-4 h-4 mr-1.5" /> Centres</Button>}
          {['SuperAdmin', 'CEO'].includes(user?.role) && <Button data-testid="manage-blocker-btn" onClick={() => { setShowBlockerPanel(!showBlockerPanel); setShowDesigPanel(false); setShowCoursesPanel(false); setShowCentresPanel(false); }} variant="outline" className="text-sm border-red-300 text-red-700 hover:bg-red-50"><Clock className="w-4 h-4 mr-1.5" /> Report Blocker</Button>}
          {canEdit && <Button data-testid="sync-users-btn" onClick={handleSync} disabled={syncing} className="bg-blue-600 text-white hover:bg-blue-700 text-sm"><RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync from HIMT'}</Button>}
        </div>
      </div>

      {syncResult && <div className={`px-4 py-3 rounded-lg text-sm ${syncResult.synced !== undefined ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>{syncResult.message}</div>}

      {/* Designation Panel */}
      {showDesigPanel && (
        <Card data-testid="designation-panel" className="bg-white border-2 border-slate-300 shadow-sm">
          <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Manage Designations</CardTitle><button onClick={() => setShowDesigPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button></div></CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input data-testid="new-designation-input" value={newDesig} onChange={e => setNewDesig(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddDesig()} placeholder="New designation..." className="border-slate-300 text-sm" />
              <Button onClick={handleAddDesig} className="bg-slate-900 text-white text-sm px-4"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">{designations.map(d => (<span key={d} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-1.5 rounded-md">{d}{canEdit && <button onClick={() => handleDeleteDesig(d)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>}</span>))}</div>
          </CardContent>
        </Card>
      )}

      {/* Courses Panel (Super Admin only) */}
      {showCoursesPanel && (
        <Card data-testid="courses-panel" className="bg-white border-2 border-cyan-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-cyan-600" /><CardTitle className="text-sm font-bold uppercase tracking-wider text-cyan-700">Manage Courses</CardTitle></div>
              <button onClick={() => setShowCoursesPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Create courses (e.g., "Pre-Sea DNS", "GME"), then assign them to CIC users in the Staff Directory below. Course names will appear next to the CIC role wherever shown.</p>
            <div className="flex gap-2 mb-3">
              <Input data-testid="new-course-name" value={newCourse.name} onChange={e => setNewCourse(prev => ({ ...prev, name: e.target.value }))} placeholder="Course name (e.g. GME)" className="border-slate-300 text-sm" />
              <Input data-testid="new-course-code" value={newCourse.code} onChange={e => setNewCourse(prev => ({ ...prev, code: e.target.value }))} placeholder="Code (optional)" className="border-slate-300 text-sm w-32" />
              <Button data-testid="add-course-btn" onClick={handleAddCourse} className="bg-cyan-600 text-white text-sm px-4"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {courses.map(c => (
                <span key={c._id} data-testid={`course-chip-${c._id}`} className="inline-flex items-center gap-1.5 bg-cyan-50 text-cyan-700 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-cyan-100">
                  {c.name}{c.code && <span className="text-cyan-500 text-[10px]">({c.code})</span>}
                  <button onClick={() => handleDeleteCourse(c._id, c.name)} data-testid={`delete-course-${c._id}`} className="text-cyan-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {courses.length === 0 && <p className="text-xs text-slate-400">No courses yet. Add your first one above.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Centres Panel (Super Admin only) */}
      {showCentresPanel && (
        <Card data-testid="centres-panel" className="bg-white border-2 border-blue-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" /><CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-700">Manage Centres</CardTitle></div>
              <button onClick={() => setShowCentresPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Centres are used for filtering reports and assigning staff. Add or remove centres as your campus list changes.</p>
            <div className="flex gap-2 mb-3">
              <Input data-testid="new-centre-input" value={newCentre} onChange={e => setNewCentre(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddCentre()} placeholder="New centre name..." className="border-slate-300 text-sm" />
              <Button data-testid="add-centre-btn" onClick={handleAddCentre} className="bg-blue-600 text-white text-sm px-4"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {centres.map(c => (
                <span key={c} data-testid={`centre-chip-${c}`} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-blue-100">
                  {c}
                  <button onClick={() => handleDeleteCentre(c)} data-testid={`delete-centre-${c}`} className="text-blue-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {centres.length === 0 && <p className="text-xs text-slate-400">No centres defined.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Blocker Settings Panel */}
      {showBlockerPanel && (
        <Card data-testid="blocker-panel" className="bg-white border-2 border-red-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-600" />
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-red-600">Report Backdating Blocker</CardTitle>
              </div>
              <button onClick={() => setShowBlockerPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-4">Set the maximum number of days each role can backdate their report entries. Staff cannot create reports older than this many days.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'].map(role => (
                <div key={role} className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${ROLE_COLORS[role]}`}>{ROLE_LABELS[role]}</span>
                  <input
                    data-testid={`blocker-days-${role}`}
                    type="number"
                    min="0"
                    max="365"
                    value={blockerDays[role] ?? ''}
                    onChange={e => setBlockerDays(prev => ({ ...prev, [role]: e.target.value }))}
                    placeholder="No limit"
                    className="w-20 h-7 text-xs text-center border border-slate-300 rounded px-1 focus:border-red-400 focus:outline-none"
                  />
                  <span className="text-[10px] text-slate-400">days</span>
                  <button
                    data-testid={`blocker-save-${role}`}
                    onClick={() => handleSaveBlocker(role)}
                    disabled={blockerSaving[role]}
                    className="ml-auto text-[10px] font-bold text-white bg-red-600 hover:bg-red-700 rounded px-2 py-1 disabled:opacity-50 shrink-0"
                  >
                    {blockerSaving[role] ? '...' : 'Set'}
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {syncStatus && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Users</p><p className="text-2xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{syncStatus.total_users}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Synced</p><p className="text-2xl font-black text-blue-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{syncStatus.synced_users}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Manual</p><p className="text-2xl font-black text-emerald-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{syncStatus.manual_users}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Roles</p><div className="flex flex-wrap gap-1 mt-1">{Object.entries(syncStatus.role_breakdown || {}).map(([role, count]) => (<span key={role} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ROLE_COLORS[role] || 'bg-slate-100 text-slate-600'}`}>{ROLE_LABELS[role] || role}: {count}</span>))}</div></CardContent></Card>
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="user-search" type="text" placeholder="Search by name, email, department..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-slate-300" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Role filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400" />
            {availableRoles.map(r => (
              <button key={r} data-testid={`user-role-filter-${r.toLowerCase()}`} onClick={() => setRoleFilter(r)} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${roleFilter === r ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{r === 'All' ? 'All Roles' : ROLE_LABELS[r] || r}</button>
            ))}
          </div>
          {/* Centre filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <MapPin className="w-4 h-4 text-slate-400" />
            {['All', ...centres].map(c => (
              <button key={c} data-testid={`centre-filter-${c.toLowerCase()}`} onClick={() => setCentreFilter(c)} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${centreFilter === c ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>{c === 'All' ? 'All Centres' : c}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Staff Table — Inline Editable */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between"><div className="flex items-center gap-2"><Users className="w-4 h-4 text-slate-700" /><CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Staff Directory</CardTitle></div><span className="text-xs text-slate-400 font-medium">{filteredUsers.length} of {users.length}</span></div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[140px]">Name</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[160px]">Email</th>
                  <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[110px]">Role</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[130px]">Designation</th>
                  {user?.role === 'SuperAdmin' && <th className="text-left text-[10px] font-bold uppercase tracking-wider text-cyan-700 px-3 py-2.5 min-w-[140px]">Courses (CIC)</th>}
                  <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[110px]">Centre</th>
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[160px]">Reports To</th>
                  {user?.role === 'SuperAdmin' && <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 min-w-[90px]">Status</th>}
                  <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2.5 w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map(u => {
                  const edited = getEdited(u);
                  const currentRole = edited.role !== undefined ? edited.role : u.role;
                  const currentDesig = edited.designation !== undefined ? edited.designation : (u.designation || '');
                  const currentCentre = edited.centre !== undefined ? edited.centre : (u.centre || '');
                  const currentIncharges = edited.incharge_ids !== undefined ? edited.incharge_ids : (u.incharge_ids || []);
                  const currentCourseIds = edited.course_ids !== undefined ? edited.course_ids : (u.course_ids || []);
                  const currentAlsoFaculty = edited.also_faculty !== undefined ? edited.also_faculty : !!u.also_faculty;
                  const changed = hasChanges(u);
                  const isSaving = savingRows[u._id];

                  return (
                    <tr key={u._id} data-testid={`user-row-${u._id}`} className={`border-b border-slate-100 transition-colors ${changed ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2 font-medium text-slate-800 text-xs">
                        {u.name}
                        {currentRole === 'CIC' && (u.course_names || []).length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {u.course_names.map((cn, ci) => (
                              <span key={ci} className="bg-cyan-50 text-cyan-700 text-[9px] font-semibold px-1 py-0.5 rounded border border-cyan-100">{cn}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-[11px]">{u.email}</td>
                      <td className="px-3 py-1.5 text-center">
                        {canEdit ? (
                          <div className="flex flex-col items-center gap-1">
                            <select data-testid={`role-select-${u._id}`} value={currentRole} onChange={e => setField(u._id, 'role', e.target.value)} className={`text-[10px] font-bold rounded px-1.5 py-1 border-0 cursor-pointer ${ROLE_COLORS[currentRole] || 'bg-slate-100 text-slate-600'}`}>
                              {SYSTEM_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                            </select>
                            {currentRole !== 'Faculty' && (
                              <label className="flex items-center gap-1 text-[9px] text-slate-500 cursor-pointer" title="Also allow this user to create & submit their own daily reports">
                                <input type="checkbox" data-testid={`also-faculty-${u._id}`} checked={currentAlsoFaculty} onChange={e => setField(u._id, 'also_faculty', e.target.checked)} className="accent-blue-600 w-3 h-3" />
                                + Faculty
                              </label>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600'}`}>{ROLE_LABELS[u.role] || u.role}</span>
                            {u.also_faculty && u.role !== 'Faculty' && <span className="text-[9px] font-semibold text-blue-600">+ Faculty</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {canEdit ? (
                          <select value={currentDesig} onChange={e => setField(u._id, 'designation', e.target.value)} className="w-full text-xs border border-slate-200 rounded px-1.5 py-1 bg-white focus:border-slate-400">
                            <option value="">—</option>
                            {designations.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        ) : <span className="text-xs text-slate-600">{u.designation || '—'}</span>}
                      </td>
                      {user?.role === 'SuperAdmin' && (
                        <td className="px-3 py-1.5">
                          {currentRole === 'CIC' ? (
                            <CoursePicker
                              userId={u._id}
                              currentIds={currentCourseIds}
                              allCourses={courses}
                              onChange={(ids) => setField(u._id, 'course_ids', ids)}
                            />
                          ) : <span className="text-[10px] text-slate-300 italic">N/A</span>}
                        </td>
                      )}
                      <td className="px-3 py-1.5 text-center">
                        {canEdit ? (
                          <div className="flex flex-col items-center gap-1">
                            <select data-testid={`centre-select-${u._id}`} value={currentCentre} onChange={e => setField(u._id, 'centre', e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white focus:border-slate-400">
                              <option value="">—</option>
                              {centres.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            {user?.role === 'SuperAdmin' && ['AcademicHead', 'PrincipalSSV', 'CEO'].includes(currentRole) && (
                              <AssignedCentresSelect
                                userId={u._id}
                                currentCentres={editedRows[u._id]?.assigned_centres !== undefined ? editedRows[u._id].assigned_centres : (u.assigned_centres || [])}
                                allCentres={centres}
                                onChange={(arr) => setField(u._id, 'assigned_centres', arr)}
                              />
                            )}
                          </div>
                        ) : <span className="text-xs text-slate-600">{u.centre || '—'}</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        {canEdit ? (
                          <InchargeSelect
                            userId={u._id}
                            currentIds={currentIncharges}
                            allUsers={users}
                            onSave={(ids) => setField(u._id, 'incharge_ids', ids)}
                          />
                        ) : (
                          <div className="flex flex-wrap gap-0.5">
                            {(u.incharge_ids || []).map(id => { const ic = users.find(x => x._id === id); return ic ? <span key={id} className="bg-indigo-50 text-indigo-600 px-1 py-0.5 rounded text-[9px] font-semibold">{ic.name}</span> : null; })}
                            {(!u.incharge_ids || u.incharge_ids.length === 0) && <span className="text-slate-400 text-xs">—</span>}
                          </div>
                        )}
                      </td>
                      {user?.role === 'SuperAdmin' && (() => {
                        const currentActive = editedRows[u._id]?.is_active !== undefined ? editedRows[u._id].is_active : (u.is_active !== false);
                        return (
                          <td className="px-3 py-1.5 text-center">
                            <button
                              data-testid={`active-toggle-${u._id}`}
                              onClick={() => setField(u._id, 'is_active', !currentActive)}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1.5 rounded-full transition-colors ${
                                currentActive ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                              title={currentActive ? 'Click to deactivate' : 'Click to activate'}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${currentActive ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                              {currentActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                        );
                      })()}
                      <td className="px-3 py-1.5 text-center">
                        <div className="flex items-center gap-1 justify-center">
                          {canEdit && changed && (
                            <button data-testid={`save-row-${u._id}`} onClick={() => handleSaveRow(u)} disabled={isSaving} className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded px-2 py-1 transition-colors disabled:opacity-50">
                              {isSaving ? '...' : <><Check className="w-3 h-3" /> Save</>}
                            </button>
                          )}
                          {user?.role === 'SuperAdmin' && u.email !== 'superadmin@himt.edu' && (
                            <button
                              data-testid={`reset-pwd-${u._id}`}
                              onClick={async () => {
                                if (!window.confirm(`Reset password for ${u.name} (${u.email}) back to default 'Himt@123'?`)) return;
                                try {
                                  await api.resetUserPassword(u._id);
                                  toast.success(`Password reset to Himt@123 for ${u.email}`);
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Reset failed');
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded px-2 py-1 transition-colors"
                              title="Reset password to default 'Himt@123'"
                            >
                              <KeyRound className="w-3 h-3" /> Reset PW
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredUsers.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <span className="text-xs text-slate-500">Page {page} of {totalPages} ({filteredUsers.length} total)</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">First</button>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">Prev</button>
                  <span className="text-xs font-semibold text-slate-700 px-2">{page}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">Next</button>
                  <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">Last</button>
                </div>
              </div>
            )}
            {filteredUsers.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No users found.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
